import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo, getIntersectionRoot } from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();
  #loadingNext = false;
  #loadingPrevious = false;
  #generation = 0;
  #autoPaused = false;
  #scrollFrame = 0;

  #handleMoreClick = (event) => {
    const link = event.target.closest('[data-load-more]');
    if (!link || !this.contains(link) || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    this.#autoPaused = false;
    this.#renderNextPage();
  };

  // Element scroll events do not bubble. Capture also handles nested desktop
  // scrollers, independently of IntersectionObserver/browser layout differences.
  #handleScroll = () => {
    if (!CSS.supports('content-visibility', 'auto')) return;
    if (this.#scrollFrame || this.#autoPaused || this.#loadingNext) return;
    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = 0;
      const marker = this.refs.viewMoreNext;
      if (!marker || marker.hidden || !this.isConnected) return;
      const rect = marker.getBoundingClientRect();
      if (rect.width > 0 && rect.bottom > 0 && rect.top <= window.innerHeight + 100) this.#renderNextPage();
    });
  };

  #updateMore(error = false) {
    const marker = this.refs.viewMoreNext;
    if (!marker) return;
    const next = this.#getPage('next');
    marker.hidden = !this.#shouldUsePage(next);
    const link = marker.querySelector('[data-load-more]');
    const direct = marker.querySelector('[data-pagination-direct]');
    const status = marker.querySelector('[data-pagination-status]');
    if (link) {
      if (next) link.href = next.url.toString();
      link.textContent = this.#loadingNext ? 'Se încarcă…' : error ? 'Reîncearcă încărcarea' : 'Încarcă mai multe produse';
      link.setAttribute('aria-disabled', String(this.#loadingNext));
    }
    if (direct) {
      if (next) direct.href = next.url.toString();
      direct.hidden = !error;
    }
    if (status) status.textContent = error ? 'Produsele nu au putut fi încărcate. Reîncearcă sau deschide pagina următoare.' : '';
  }

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#observeViewMore();
    this.addEventListener('click', this.#handleMoreClick);
    document.addEventListener('scroll', this.#handleScroll, { capture: true, passive: true });
    window.addEventListener('resize', this.#handleScroll);

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#generation++;
    this.removeEventListener('click', this.#handleMoreClick);
    document.removeEventListener('scroll', this.#handleScroll, true);
    window.removeEventListener('resize', this.#handleScroll);
    cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = 0;
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    this.#updateMore();
    // Older WebKit uses the explicit load-more link, avoiding automatic batches.
    if (!CSS.supports('content-visibility', 'auto')) return;
    const { viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMoreNext || !('IntersectionObserver' in window)) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMoreNext } = this.refs;

              if (entry.target === viewMoreNext && !this.#autoPaused) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          root: null,
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const generation = this.#generation;
    let timer;
    try {
      const pageContent = await Promise.race([
        sectionRenderer.getSectionHTML(this.sectionId, true, new URL(pageInfo.url.toString())),
        new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('Pagination timeout')), 15000); }),
      ]);
      if (generation === this.#generation) this.pages.set(pageNumber, pageContent);
    } finally {
      clearTimeout(timer);
    }
  }

  async #renderNextPage() {
    if (this.#loadingNext) return;
    this.#loadingNext = true;
    this.#updateMore();
    try {
      await this.#appendNextPage();
    } catch (error) {
      this.#autoPaused = true;
    } finally {
      this.#loadingNext = false;
      this.#updateMore(this.#autoPaused);
    }
  }

  async #appendNextPage() {
    const generation = this.#generation;
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      await this.#fetchSpecificPage(nextPage.page, nextPage.url);
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (generation !== this.#generation || grid !== this.refs.grid) return;
      if (!nextPageItemElements?.length) throw new Error('Empty pagination response');
    }

    // Observer callbacks can overlap while a fetch is pending. Never insert
    // an already loaded page or a response belonging to the previous filter.
    if (grid !== this.refs.grid || this.#getPage('next')?.page !== nextPage.page) return;
    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    await yieldToMainThread();

    history.pushState('', '', nextPage.url.toString());

  }

  async #renderPreviousPage() {
    if (this.#loadingPrevious) return;
    this.#loadingPrevious = true;
    try {
      await this.#prependPreviousPage();
    } catch (error) {
      // Keep the page usable when fetching an earlier page fails.
    } finally {
      this.#loadingPrevious = false;
    }
  }

  async #prependPreviousPage() {
    const generation = this.#generation;
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      await this.#fetchSpecificPage(previousPage.page, previousPage.url);
      if (generation !== this.#generation) return;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    if (grid !== this.refs.grid || this.#getPage('previous')?.page !== previousPage.page) return;
    // Store the current scroll position and height of the first element
    const currentScrollTop = getScrollTop();
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
      const heightDiff = newHeight - oldHeight;
      scrollTo({
        top: currentScrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    await yieldToMainThread();

    history.pushState('', '', previousPage.url.toString());

  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.pages.clear();
    this.#generation++;
    this.#autoPaused = false;

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage || this.refs.grid !== grid || this.refs.cards?.[0] !== firstCard) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#handleScroll();
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    const firstCard = this.refs.cards?.[0];
    if (grid) {
      observer.observe(this, {
        subtree: true,
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
